import React, { useState, useEffect, useCallback } from 'react';
import { History, RotateCcw, ChevronDown, ChevronUp, ArrowLeftRight, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VersionHistory({ entityType, entityId, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [compareResult, setCompareResult] = useState(null);

  const token = localStorage.getItem('systemklart_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/v2/versions/${entityType}/${entityId}`, { headers });
      setVersions(res.data.versions);
    } catch (e) {
      console.error('Failed to load versions:', e);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (expanded && versions.length === 0) fetchVersions();
  }, [expanded, fetchVersions, versions.length]);

  const viewSnapshot = async (version) => {
    try {
      const res = await axios.get(`${API}/api/v2/versions/${entityType}/${entityId}/${version}`, { headers });
      setSelectedSnapshot(res.data);
      setCompareResult(null);
    } catch (e) {
      toast.error('Kunne ikke hente versjon');
    }
  };

  const compareVersions = async (v1, v2) => {
    try {
      const res = await axios.get(`${API}/api/v2/versions/${entityType}/${entityId}/compare/${v1}/${v2}`, { headers });
      setCompareResult(res.data);
      setSelectedSnapshot(null);
    } catch (e) {
      toast.error('Kunne ikke sammenligne versjoner');
    }
  };

  const restoreVersion = async (version) => {
    if (!window.confirm(`Gjenopprette til versjon ${version}? Nåværende data lagres som ny versjon.`)) return;
    try {
      await axios.post(`${API}/api/v2/versions/${entityType}/${entityId}/${version}/restore`, {}, { headers });
      toast.success(`Gjenopprettet til versjon ${version}`);
      fetchVersions();
      if (onRestored) onRestored();
    } catch (e) {
      toast.error('Kunne ikke gjenopprette');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('nb-NO')} ${d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const FIELD_LABELS = {
    title: 'Tittel', description: 'Beskrivelse', status: 'Status',
    severity: 'Alvorlighetsgrad', priority: 'Prioritet', deadline: 'Frist',
    assigned_to: 'Tildelt', type: 'Type', content: 'Innhold',
    corrective_action: 'Korrigerende tiltak', preventive_action: 'Forebyggende tiltak',
    root_cause: 'Rotårsak', notes: 'Notater',
  };

  return (
    <div className="mt-4" data-testid="version-history">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors mb-2"
        data-testid="version-toggle"
      >
        <History className="w-4 h-4" />
        <span>Endringshistorikk {versions.length > 0 ? `(${versions.length})` : ''}</span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="space-y-2">
          {loading && <p className="text-xs text-slate-500">Laster...</p>}
          {!loading && versions.length === 0 && (
            <p className="text-xs text-slate-500">Ingen tidligere versjoner.</p>
          )}

          {versions.map((v, i) => (
            <div
              key={v.version}
              className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-3 flex items-center justify-between"
              data-testid={`version-${v.version}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">v{v.version}</span>
                <div>
                  <span className="text-sm text-slate-300">{v.changed_by}</span>
                  <span className="text-xs text-slate-500 ml-2">{formatDate(v.created_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => viewSnapshot(v.version)}
                  className="px-2 py-1 text-xs text-slate-400 hover:text-white bg-slate-700/50 rounded-lg hover:bg-slate-700"
                  data-testid={`view-version-${v.version}`}
                >
                  Se
                </button>
                {i < versions.length - 1 && (
                  <button
                    onClick={() => compareVersions(versions[i + 1].version, v.version)}
                    className="px-2 py-1 text-xs text-slate-400 hover:text-white bg-slate-700/50 rounded-lg hover:bg-slate-700"
                    title="Sammenlign med forrige"
                    data-testid={`compare-version-${v.version}`}
                  >
                    <ArrowLeftRight className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={() => restoreVersion(v.version)}
                  className="px-2 py-1 text-xs text-amber-400 hover:text-amber-300 bg-amber-500/10 rounded-lg hover:bg-amber-500/20"
                  data-testid={`restore-version-${v.version}`}
                >
                  <RotateCcw className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}

          {/* Snapshot viewer */}
          {selectedSnapshot && (
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 mt-3" data-testid="snapshot-viewer">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-white">
                  Versjon {selectedSnapshot.version} — {selectedSnapshot.changed_by}
                </h4>
                <button onClick={() => setSelectedSnapshot(null)} className="text-slate-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto text-xs">
                {Object.entries(selectedSnapshot.snapshot || {}).filter(([k]) =>
                  !['_id', 'id', 'org_id', 'created_at', '_restored_from_version', '_restored_by', '_restored_at'].includes(k)
                ).map(([key, value]) => (
                  <div key={key} className="flex gap-2">
                    <span className="text-slate-500 font-medium w-32 flex-shrink-0">{FIELD_LABELS[key] || key}</span>
                    <span className="text-slate-300 truncate">{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Compare viewer */}
          {compareResult && (
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 mt-3" data-testid="compare-viewer">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-white">
                  Endringer: v{compareResult.v1.version} → v{compareResult.v2.version}
                </h4>
                <button onClick={() => setCompareResult(null)} className="text-slate-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {compareResult.changes.length === 0 ? (
                <p className="text-xs text-slate-500">Ingen endringer.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {compareResult.changes.map((change, i) => (
                    <div key={i} className="text-xs border-b border-slate-800 pb-2">
                      <span className="text-slate-400 font-medium">{FIELD_LABELS[change.field] || change.field}</span>
                      <div className="flex gap-4 mt-1">
                        <div className="flex-1">
                          <span className="text-red-400/60 line-through">{String(change.v1_value ?? '-')}</span>
                        </div>
                        <div className="flex-1">
                          <span className="text-emerald-400">{String(change.v2_value ?? '-')}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
